import React, { useState } from 'react';
import { Mail, Phone, User, MessageSquare, Loader2, Building2 } from 'lucide-react';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';

interface ContactFormProps {
  apartmentNumber?: string;
  apartmentEntrance?: string;
}

const ContactForm: React.FC<ContactFormProps> = ({ apartmentNumber, apartmentEntrance }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    apartmentNumber: apartmentNumber || '',
    apartmentEntrance: apartmentEntrance || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      console.log('Submitting form data:', formData); // Debug log
      await addDoc(collection(db, 'contacts'), {
        ...formData,
        submittedAt: new Date(),
        isRead: false
      });

      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
        apartmentNumber: apartmentNumber || '',
        apartmentEntrance: apartmentEntrance || ''
      });

      setSubmitStatus({
        type: 'success',
        message: 'Благодарим ви! Ще се свържем с вас скоро.'
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Възникна грешка. Моля, опитайте отново.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section className="py-12 md:py-20 bg-white" id="contact">
      <div className="container-custom max-w-lg mx-auto px-4">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            Свържете се с нас
          </h2>
          <p className="text-gray-600">
            Имате въпроси? Ще се радваме да помогнем.
          </p>
        </div>
        
        {submitStatus.type && (
          <div
            className={`mb-6 p-4 rounded-lg text-center ${
              submitStatus.type === 'success'
                ? 'bg-green-50 text-green-800'
                : 'bg-red-50 text-red-800'
            }`}
          >
            {submitStatus.message}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <User className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Вашето име"
              className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-base"
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Mail className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Вашият имейл"
              className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-base"
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Phone className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Телефон за връзка"
              className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-base"
              required
              disabled={isSubmitting}
            />
          </div>

          {apartmentNumber && (
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Building2 className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                name="apartmentNumber"
                value={formData.apartmentNumber}
                onChange={handleChange}
                placeholder={`Апартамент ${apartmentNumber}${apartmentEntrance ? ` - Вход ${apartmentEntrance === '1' ? 'А' : 'Б'}` : ''}`}
                className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-base bg-gray-50"
                readOnly
              />
            </div>
          )}

          <div className="relative">
            <div className="absolute top-3 left-3 pointer-events-none">
              <MessageSquare className="h-5 w-5 text-gray-400" />
            </div>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Вашето съобщение"
              rows={4}
              className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-base resize-none"
              required
              disabled={isSubmitting}
            ></textarea>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-primary text-white px-6 py-4 rounded-lg font-medium hover:bg-primary-dark transition duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed text-base"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Изпращане...
              </>
            ) : (
              'Изпрати съобщение'
            )}
          </button>
        </form>
      </div>
    </section>
  );
};

export default ContactForm;